import TemplateView from '../../../../TemplateView'
import EmptyHighlighter from "../../../../util/EmptyHighlighter"
import TableCell from "./TableCell"
import TableRow from "./TableRow"
import NameCellContent from "../../userList/table/NameCellContent"
import {canAccessAllWorkScope, doesUserCanEnterTimeTrack} from "../../utils"
import TokensHighlighter from "../../../../util/TokensHighlighter"
import StringTokenizer from "../../../../util/StringTokenizer"
import WarningsController from "../../WarningsController"
import messageResource from "../../../../messageResource"
import {HierarchyLevels, USERS_WITHOUT_GROUP_ID} from 'constants'
import {isArraysWithIdsItemsOrderEqual, isMapsWithIdsItemsOrderEqual} from "../../../../atUtils"
import WorkAssignmentsCellContent from "./WorkAssignmentsCellContent"
import {getInstance as getTooltipInstance} from '../../../../SimpleSingletonTooltip'
import {setEventPreventHidingEditUserSlidingPanel, setEventPreventHidingWorkAssignmentSlidingPanel} from "../../../../panels/utils"

export default class TableBody extends TemplateView {
    constructor({
        onNameCellClick = (id) => {},
        onWorkAssignmentsCellClick = (id) => {}
    }) {
        super(null)

        this.state = {
            users: [],
            userGroups: {},
            timeZoneGroups: {},
            usersWithoutApproversSet: [],
            userGroupsEnabled: false,
            timeZoneGroupsEnabled: false,
            wordsFilter: '',
            warningMsgsByUserId: {},
            editingUserId: null,
            newUsersIdSet: {},
            viewerManagementCounters: {},
            usersWorkAssignmentsCounters: {},
            allowedToManageAccountSettings: false,
            isURAEnabled: false,
            isBTYEnabled: false,
        }

        this.params = {
            onNameCellClick,
            onWorkAssignmentsCellClick,
        }

        this._rowsMap = {}
        this._nameCellsMap = {}
        this._waCellsMap = {}

        this._highlighter = new EmptyHighlighter()
        this._warningsController = new WarningsController()

        this.onStatePropertyChanged('wordsFilter', (wordsFilter) => {
            const tokens = new StringTokenizer(StringTokenizer.PUNCTUATORS_FOR_SEARCH).getTokens(wordsFilter.toLowerCase())
            this._highlighter = tokens && tokens.length > 0
                ? new TokensHighlighter(tokens, StringTokenizer.PUNCTUATORS_FOR_SEARCH)
                : new EmptyHighlighter()
        })

        this.onStateChanged((state, prevState) => {
            const {users} = state
            const prevUsers = prevState.users
            if (!isArraysWithIdsItemsOrderEqual(users, prevUsers)
                || !isMapsWithIdsItemsOrderEqual(state.usersWorkAssignmentsCounters, prevState.usersWorkAssignmentsCounters)) {
                this._renderTable()
            }

            users.forEach((user) => {
                const id = user.id

                if(this._rowsMap[id] && this._nameCellsMap[id] && this._waCellsMap[id]) {
                    this._setRowState(this._rowsMap[id], user)
                    this._setNameCellState(this._nameCellsMap[id], user)
                    this._setWACellState(this._waCellsMap[id], user)
                }
            })
        })

        this.onStatePropertyChanged('editingUserId', (editingUserId, prevEditingUserId) => {
            if (editingUserId !== null) {
                this._setRowSelectedState(editingUserId, true)
            }

            if (prevEditingUserId !== null) {
                this._setRowSelectedState(prevEditingUserId, false)
            }
        })
    }

    templateHtml() {
        return '<tbody class="tableBody"></tbody>'
    }

    _renderTable() {
        this.$el.empty()
        this._rowsMap = {}
        this._nameCellsMap = {}
        this._waCellsMap = {}

        this.state.users.forEach(user => {
            if(this.isRenderTableLine(user)){
                const waCell = new WorkAssignmentsCellContent({
                    onMouseIn: (e) => {
                        this._handleHoveredWACell(user.id, true, e)
                    },
                    onMouseOut: (e) => {
                        this._handleHoveredWACell(user.id, false, e)
                    },
                    onMouseMove: (e) => {
                        this._handleMousemoveWACell(e)
                    },
                    onCustomersClick: (e) => this._handleCustomersWAClick(user.id, e),
                    onProjectsClick: (e) => this._handleProjectsWAClick(user.id, e),
                    onTasksClick: (e) => this._handleTasksWAClick(user.id, e),
                })
                const nameCell = new NameCellContent({
                    warningsController: this._warningsController,
                    onClick: (e) => {
                        if (user.editable && !user.locked)
                            this._handleNameCellClick(user.id, e)
                    },
                    onMouseIn: () => {
                        if (user.editable && !user.locked)
                            this._handleHoveredNameCell(user.id, true)
                    },
                    onMouseOut: () => {
                        if (user.editable && !user.locked)
                            this._handleHoveredNameCell(user.id, false)
                    },
                })
                const cells = [
                    new TableCell({
                        cellClass: 'first userNameCell',
                        content: nameCell,
                    }),
                    new TableCell({
                        cellClass: 'emptyCell'
                    }),
                    new TableCell({
                        cellClass: 'last taskWorkAssignmentsCell enabled',
                        content: waCell
                    })
                ]
                const row = new TableRow({
                    cells: cells,
                }).init(this.$el)


                this._setRowState(row, user)
                this._setNameCellState(nameCell, user)
                this._setWACellState(waCell, user)

                this._rowsMap[user.id] = row
                this._nameCellsMap[user.id] = nameCell
                this._waCellsMap[user.id] = waCell

                if (this.state.editingUserId !== null) {
                    this._setRowSelectedState(this.state.editingUserId, true)
                }
            }
        })
    }

    isRenderTableLine(user) {
        return this.state.usersWorkAssignmentsCounters[user.id]
               || canAccessAllWorkScope({userInfo: user, isURAEnabled: this.state.isURAEnabled, isBTYEnabled: this.state.isBTYEnabled})
               || !doesUserCanEnterTimeTrack(user)
    }

    _setRowState = (row, user) => {
        row.setState({
            enabled: user.enabled,
            enabledInOtherProduct: user.enabledInOtherProduct,
            locked: user.locked,
            isNew: !!this.state.newUsersIdSet[user.id]
        })
    }

    _setNameCellState = (nameCell, user) => {
        const warningMsgs = this.state.warningMsgsByUserId[user.id]
        nameCell.setState({
            userGroupName: this._getUserGroupName(user.userGroupId),
            timeZoneGroupName: this._getTimeZoneGroupName(user.timeZoneGroupId),
            infos: warningMsgs.infoMsgs,
            warnings: warningMsgs.warningMsgs,
            firstName: user.firstName,
            lastName: user.lastName,
            middleInitial: user.middleInitial,
            username: user.username,
            withoutApprover: user.requiresTTApproval && !!this.state.usersWithoutApproversSet[user.id],
            highlighter: this._highlighter,
            enabled: user.enabled,
            locked: user.locked,
            enabledInAp: user.enabledInOtherProduct,
            withoutWorkScope: doesUserCanEnterTimeTrack(user) ? this._doesUserWithoutWorkScope(user) : false,
            clickable: user.editable,
        })
    }

    _setWACellState = (waCell, user) => {
        waCell.setState({
            viewerManagementCounters: this.state.viewerManagementCounters,
            ...this._getUserWorkAssignmentsCounters(user.id),
            allScope: canAccessAllWorkScope({userInfo: user, isURAEnabled: this.state.isURAEnabled, isBTYEnabled: this.state.isBTYEnabled}),
            noEnterTTPermission: !doesUserCanEnterTimeTrack(user),
        })
    }

    _getUserWorkAssignmentsCounters = (userId) => {
        let counters = this.state.usersWorkAssignmentsCounters[userId]
        if (!counters) {
            counters = {
                customers: 0,
                projects: 0,
                tasks: 0,
            }
        }
        return counters
    }

    _doesUserWithoutWorkScope = (user) => {
        return !canAccessAllWorkScope({userInfo: user, isURAEnabled: this.state.isURAEnabled, isBTYEnabled: this.state.isBTYEnabled})
               && !user.hasAssignedWorkScope
    }

    _setRowSelectedState = (userId, selected) => {
        const row = this._rowsMap[userId]
        if (row) {
            row.setState({
                selected: selected,
            })
        }
    }

    _handleHoveredNameCell = (id, hovered) => {
        this._rowsMap[id].setState({
            hoveredCls: hovered ? 'nameCellHovered' : ''
        })
    }

    _handleNameCellClick = (id, e) => {
        setEventPreventHidingEditUserSlidingPanel(e)
        this.params.onNameCellClick(id)
    }

    _handleHoveredWACell = (id, hovered) => {
        const user = this._getUser(id)

        if (doesUserCanEnterTimeTrack(user)) {
            if (canAccessAllWorkScope({userInfo: user, isURAEnabled: this.state.isURAEnabled, isBTYEnabled: this.state.isBTYEnabled})) {
                const tooltip = getTooltipInstance()
                if (hovered) {
                    tooltip.setState({
                        text: messageResource.getMessage('userlist.work_assignments.table.tooltip.all_scope', [user.firstName]),
                        extraClass: 'userlist_workAssignmentsTab_allScopeTooltip',
                    })
                    tooltip.show()
                } else {
                    tooltip.hide()
                }
            } else
                this._rowsMap[id].setState({
                    hoveredCls: hovered ? 'workAssignmentsCellHovered' : ''
                })
        }
    }

    _handleMousemoveWACell = (e) => {
        getTooltipInstance().moveAfterCursor(e)
    }

    _getUser = (id) => {
        return this.state.users.find((user) => id === user.id)
    }

    _getUserGroupName = (groupId) => {
        if (this.state.userGroupsEnabled) {
            return groupId === USERS_WITHOUT_GROUP_ID
                ? messageResource.getMessage("userlist.js.table.username_cell.user_without_group")
                : (!!this.state.userGroups[groupId] ? this.state.userGroups[groupId].name : '')
        } else {
            return ''
        }
    }

    _getTimeZoneGroupName = (tzGroupId) => {
        if (this.state.timeZoneGroupsEnabled) {
            return (!!this.state.timeZoneGroups[tzGroupId] ? this.state.timeZoneGroups[tzGroupId].name : '')
        } else {
            return ''
        }
    }

    _handleCustomersWAClick = (userId, e) => {
        setEventPreventHidingWorkAssignmentSlidingPanel(e)
        this.params.onWorkAssignmentsCellClick(userId, HierarchyLevels.CUSTOMER)
    }

    _handleProjectsWAClick = (userId, e) => {
        setEventPreventHidingWorkAssignmentSlidingPanel(e)
        this.params.onWorkAssignmentsCellClick(userId, HierarchyLevels.PROJECT)
    }

    _handleTasksWAClick = (userId, e) => {
        setEventPreventHidingWorkAssignmentSlidingPanel(e)
        this.params.onWorkAssignmentsCellClick(userId, HierarchyLevels.TASK)
    }
}



// WEBPACK FOOTER //
// ./web/js/administration/userManagement/workAssignmentsTab/components/TableBody.js